import { Input, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import IconAlertTriangle from "@src/assets/icons/tabler/alert-triangle.svg?react";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import type { z } from "zod";
import Popup from "../popup/Popup";
import { ShareWhiteboardUserCombobox } from "./share-whiteboard-combobox";
import { ShareWhiteboardUserDisplay } from "./share-whiteboard-user-display";
import type {
	Role,
	inviteFormSchema,
	userSelected,
} from "./whiteboard-invite-schema";

export function ShareInviteUser(props: {
	index: number;
	form: UseFormReturnType<z.infer<typeof inviteFormSchema>>;
	user: z.infer<typeof userSelected>;
	handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
	entityName: string;
	disableEditor?: boolean;
}) {
	const {
		form,
		user,
		entityName,
		index,
		handleSubmit,
		disableEditor = false,
	} = props;
	const authenticatedUser = useAuthenticatedUser();

	const [
		isTransferOwnershipPopupOpen,
		{ open: openTransferOwnershipPopup, close: closeTransferOwnershipPopup },
	] = useDisclosure(false);

	const handleTransferOwnershipConfirm = async () => {
		form.setFieldValue(`users.${index}.permission`, "Owner");
		closeTransferOwnershipPopup();
		handleSubmit();
	};

	const handleChange = (value: Role) => {
		switch (value) {
			case "Waiting":
				return;
			case "Owner":
				openTransferOwnershipPopup();
				return;
			case "Remove":
				form.removeListItem("users", index);
				break;
			default:
				form.setFieldValue(`users.${index}.permission`, value);
		}

		handleSubmit();
	};

	return (
		<>
			<div className="flex relative items-center max-w-full">
				{form.submitting && (
					<div className="flex absolute inset-0 z-10 justify-center items-center bg-surface-default/70 rounded-md" />
				)}

				<Input
					type="hidden"
					key={form.key(`users.${index}.email`)}
					value={user.email}
				/>

				<Input
					type="hidden"
					key={form.key(`users.${index}.name`)}
					value={user.name}
				/>

				<Input
					type="hidden"
					key={form.key(`users.${index}.avatarUrl`)}
					value={user.avatarUrl}
				/>

				<Input
					type="hidden"
					key={form.key(`users.${index}.activationStatus`)}
					value={user.activationStatus}
				/>

				<ShareWhiteboardUserDisplay
					user={user}
					isCurrentUser={authenticatedUser.email === user.email}
				/>

				<div className="shrink-0 ml-2">
					{user.permission === "Owner" ? (
						<Text fw={600} size="sm" c="dimmed">
							Owner
						</Text>
					) : (
						<ShareWhiteboardUserCombobox
							activationStatus={user.activationStatus}
							value={user.permission}
							onChange={handleChange}
							disableEditor={disableEditor}
						/>
					)}
				</div>
			</div>

			<Popup
				isOpen={isTransferOwnershipPopupOpen}
				onClose={closeTransferOwnershipPopup}
				title="Transfer ownership"
				description={`Are you sure you want to make ${user.name} the owner of ${entityName}? This action cannot be undone.`}
				IconComponent={IconAlertTriangle}
				accentColor="var(--ui-color-danger)"
				iconBackroundColor="var(--ui-color-danger-soft)"
				secondaryColor="var(--ui-color-text-tertiary)"
				actionButtonText="Transfer ownership"
				cancelButtonText="Cancel"
				onActionConfirm={handleTransferOwnershipConfirm}
				onActionCancel={closeTransferOwnershipPopup}
			/>
		</>
	);
}
